import get from 'lodash/get';
import ipaddr from 'ipaddr.js';

import checkHealth from './checkHealth';
import { updateTaskWithScreenshotError } from '../../redesign/javascript/api/TaskUpdateApi';
import {
  regnerateScreenshotFromServer,
  sendScreenshotRequest
} from './sendScreenshotRequest';
import { screenshotServerUnavailableMessage } from '../models/ScreenshotCaptureError';
import { post } from '../../redesign/javascript/utils/fetch';
import { getBugsnag } from '../models/Application';

export const isPrivateUrl = screenshotUrl => {
  try {
    const url = new URL(screenshotUrl);
    if (url.hostname === 'localhost') {
      return true;
    }

    // not an ip address, not private
    if (!ipaddr.isValid(url.hostname)) {
      return false;
    }

    const range = ipaddr.parse(url.hostname).range();

    return range === 'loopback' || range === 'private';
  } catch (e) {
    console.log(`${screenshotUrl} ${e.message}`);
  }

  return false;
};

type RequestScreenshotDataParams = {
  bugherdUrl: string;
  projectId: number;
  polyfillData: unknown;
};

export const sendScreenshotData = async ({
  bugherdUrl,
  projectId,
  polyfillData
}: RequestScreenshotDataParams) => {
  try {
    await post(`${bugherdUrl}/projects/${projectId}/screenshot_data`, {
      screenshot_data: polyfillData
    });
  } catch (e) {
    console.error(e);
    if (e instanceof Error) {
      getBugsnag().notify('Error uploading screenshot data due to' + e.message);
    } else {
      getBugsnag().notify(
        'Error uploading screenshot data due to unknown error' + e
      );
    }
  }
};

type ScreenshotData = {
  taskId: number;
  polyfillData: unknown;
  s3Response: unknown;
  screenshotServerUrl: string;
  projectId: number;
  bugherdUrl: string;
  rescueScreenshotsEnabled?: boolean;
  organizationId: number;
  userId?: number;
  screenshotKey?: string;
};
export const requestScreenshot = async (
  screenshotData: ScreenshotData
): Promise<void> => {
  const {
    taskId,
    polyfillData,
    s3Response,
    screenshotServerUrl,
    projectId,
    bugherdUrl,
    rescueScreenshotsEnabled,
    organizationId,
    userId,
    screenshotKey
  } = screenshotData;
  const screenshotUrl = get(polyfillData, 'properties.url');

  if (isPrivateUrl(screenshotUrl)) {
    console.log(`[BH] Cannot take screenshot on a private url`);
    return;
  }

  if (rescueScreenshotsEnabled) {
    const isRetry = polyfillData.requestType === 'retry';
    const { projectDetailsUrl, basicAuthPresent } = polyfillData;

    if (isRetry) {
      return regnerateScreenshotFromServer({
        screenshotServerUrl,
        projectId,
        bugherdUrl,
        taskId,
        s3Response,
        organizationId,
        userId,
        projectDetailsUrl,
        basicAuthPresent,
        screenshotKey
      });
    }
  }

  try {
    await checkHealth(screenshotServerUrl);
  } catch (error) {
    updateTaskWithScreenshotError(
      taskId,
      screenshotServerUnavailableMessage(error),
      bugherdUrl,
      projectId,
      polyfillData
    );

    return;
  }

  try {
    await sendScreenshotRequest({
      polyfillData,
      s3Response,
      taskId,
      screenshotServerUrl,
      projectId,
      bugherdUrl,
      userId
    });
  } catch (e) {
    delete polyfillData.projectDetailsUrl;
    delete polyfillData.basicAuthPresent;

    if (getBugsnag()) {
      getBugsnag().notify(e, event => {
        event.addMetadata('screenshot-health', {
          taskId: taskId,
          projectId: projectId
        });
      });

      console.error(e);
    }
  }
};
